import { Badge, Box, Button, HoverCard, Select, Tabs, Text, Tooltip } from '@mantine/core';
import { IconBug, IconListDetails, IconCheck } from '@tabler/icons-react';

import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useAwareZindex } from '~/hooks/context/useZIndex';
import type { AiChange, TestRunExecutionOutput } from '~/types';
import ErrorDetails, { checkIsErrorAutoHealed } from './ErrorDetails';

type Error = TestRunExecutionOutput['errors'][0] & { attemptId?: string; attemptIndex?: number };

export function ErrorHoverCardContent({
  errors,
  aiChanges,
  runId,
}: {
  errors: Error[];
  aiChanges: AiChange[];
  runId?: string;
}) {
  const router = useRouter();
  const projectId = useProjectId();

  const errorsByAttempt = useMemo(() => {
    return Object.groupBy(errors, (error) => error.attemptId || 'unknown');
  }, [errors.length]);

  const attemptOptions = useMemo(() => {
    return Object.keys(errorsByAttempt).map((attemptId) => {
      const errorWithIndex = errors.find((error) => (error.attemptId || 'unknown') === attemptId);
      const attemptIndex = errorWithIndex?.attemptIndex;

      return {
        value: attemptId,
        label: attemptId === 'unknown' ? 'Unknown Attempt' : `Attempt #${attemptIndex || 1}`,
      };
    });
  }, [errorsByAttempt]);

  const [selectedAttemptId, setSelectedAttemptId] = useState<string>(attemptOptions.at(0)?.value || '');

  const selectedErrors = errorsByAttempt[selectedAttemptId] || [];
  const shouldShowDetailsButton = !!runId && errors.length > 0;

  return (
    <div
      className={clsx('flex flex-col space-y-3', {
        'min-h-[300px]': shouldShowDetailsButton,
        'min-h-[200px]': !shouldShowDetailsButton,
      })}
    >
      <div className="flex-shrink-0">
        <Text
          size="sm"
          fw={500}
        >
          The following error{errors.length > 1 ? 's' : ''} occurred:
        </Text>
      </div>

      {/* Attempt Selector */}
      {attemptOptions.length > 1 && (
        <div className="flex-shrink-0">
          <Select
            data={attemptOptions}
            value={selectedAttemptId}
            defaultValue={attemptOptions[0].value}
            onChange={(value) => setSelectedAttemptId(value || 'unknown')}
            placeholder="Select attempt"
            allowDeselect={false}
            renderOption={({ option, checked }) => (
              <div className="flex items-center gap-2">
                <IconCheck
                  className={!checked ? 'invisible' : ''}
                  size={14}
                />
                <Text size="sm">{option.label}</Text>
                <Badge
                  size="xs"
                  color="red"
                  variant="light"
                >
                  {errorsByAttempt[option.value]?.length || 0}{' '}
                  {errorsByAttempt[option.value]?.length === 1 ? 'error' : 'errors'}
                </Badge>
              </div>
            )}
          />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {selectedErrors.length > 0 ?
          selectedErrors.length === 1 ?
            <SingleErrorWithStepInfo
              error={selectedErrors[0]}
              aiChanges={aiChanges}
            />
          : <AttemptErrors
              errors={selectedErrors}
              aiChanges={aiChanges}
            />

        : <Text
            size="sm"
            c="dimmed"
          >
            No error details available for this attempt
          </Text>
        }
      </div>

      {!!runId && errors.length > 0 && (
        <div className="flex-shrink-0">
          <Button
            variant="light"
            size="xs"
            component={Link}
            href={{
              pathname: router.pathname,
              query: {
                ...router.query,
                testRunDrawer: runId,
                ...(selectedAttemptId !== 'unknown' && { attemptId: selectedAttemptId }),
              },
            }}
            as={`/project/${projectId}/history/${router.query.runOrSuiteId}/run/${runId}`}
            fullWidth
            leftSection={<IconListDetails size={14} />}
          >
            View Full Details
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ErrorHoverCard({
  errors,
  aiChanges,
  runId,
  children,
}: {
  errors: Error[];
  aiChanges: AiChange[];
  runId?: string;
  children: React.ReactNode;
}) {
  const zIndexBase = useAwareZindex();

  return (
    <HoverCard
      disabled={errors.length === 0}
      width={420}
      zIndex={zIndexBase + 30}
      shadow="md"
      withArrow
      openDelay={300}
      closeDelay={400}
    >
      <HoverCard.Target>{children}</HoverCard.Target>
      <HoverCard.Dropdown>
        <ErrorHoverCardContent
          errors={errors}
          aiChanges={aiChanges}
          runId={runId}
        />
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

/**
 * Single error display with step information context.
 * Used in the hover card to show where the error occurred.
 */
const SingleErrorWithStepInfo = ({ error, aiChanges }: { error: Error; aiChanges: AiChange[] }) => {
  const isHealed = checkIsErrorAutoHealed({ error, aiChanges });

  return (
    <Box className="space-y-3">
      {/* Step Information - shown in hover card for context */}
      <div className="flex flex-col items-start gap-1 pt-1">
        <Text
          size="sm"
          fw={500}
          c="dimmed"
        >
          Step {error.stepIndex + 1}: {error.stepDescription}
        </Text>
        {!!error.operationDescription && (
          <Text
            size="xs"
            c="dimmed"
          >
            {error.operationDescription}
          </Text>
        )}
      </div>

      {/* Error Details */}
      <ErrorDetails
        error={error}
        isAutoHealed={isHealed}
      />
    </Box>
  );
};

const AttemptErrors = ({ errors, aiChanges }: { errors: Error[]; aiChanges: AiChange[] }) => (
  <Tabs
    // Default to the last error as it's the one that actually failed the test
    defaultValue={(errors.length - 1).toString()}
    className="flex h-full flex-col"
  >
    <Tabs.List className="flex-shrink-0">
      {errors.map((error, index) => {
        const isHealed = checkIsErrorAutoHealed({ error, aiChanges });
        return (
          <Tabs.Tab
            key={index}
            value={index.toString()}
            leftSection={<IconBug size={14} />}
            rightSection={
              isHealed ?
                <Tooltip label="Successfully auto-healed">
                  <div className="rounded-sm bg-violet-500/30 p-px text-violet-600 dark:bg-violet-500/80 dark:text-violet-200">
                    <IconCheck size={12} />
                  </div>
                </Tooltip>
              : undefined
            }
          >
            {`Error ${index + 1}`}
          </Tabs.Tab>
        );
      })}
    </Tabs.List>

    <div className="min-h-0 flex-1 overflow-y-auto">
      {errors.map((error, index) => (
        <Tabs.Panel
          key={index}
          value={index.toString()}
          pt="md"
        >
          <SingleErrorWithStepInfo
            error={error}
            aiChanges={aiChanges}
          />
        </Tabs.Panel>
      ))}
    </div>
  </Tabs>
);
